// @ts-strict-ignore
import Avatar from 'antd/lib/avatar';
import Text from 'antd/lib/typography/Text';
import Space from 'antd/lib/space';
import { compact } from 'lodash';
import React from 'react';
import { useSelector } from 'react-redux';
import { Link, useRouteMatch } from 'react-router-dom';
import { RootState } from '../../../reducers/reducers';
import routes from '../../../routes';
import { selectAllAppsIdsInOrg, selectApp, selectOrg, selectUser } from '../../../selectors/selectors';
import { AppRouteComponentMatchParams } from '../../../typescriptHelpers';
import Input from 'antd/lib/input';
import Icon, { CaretDownFilled, CloseCircleFilled, SearchOutlined } from '@ant-design/icons';
import { antGrey, runwayColors } from '../../styles/globalStyles';
import { useSidebarColors } from '../../styles/themeMode';
import { displayTextForAppPlatform, displayUserNameShort } from '../../../helpers/displayHelpers';
import LogoWordmark from '../../../../assets/runway-logo-horizontal.svg';
import styles from '../../styles/organizationNavigatorStyles';
import { iconComponentForPlatform } from '../../../helpers/platformComponentHelpers';
import { AppPlatform } from '../../../types';
import { createAppBuildsRoute, createOrgRoute } from '../../../helpers/routeHelpers';
import { getIsBuildDistroSupportedPlatform } from '../../../helpers';

export interface OrgOrAppMenuItemProps {
    shouldShowText?: boolean;
    onClick?: () => void;
    shouldShowCircleIcon?: boolean;
    appPlatform?: AppPlatform;
    title: string;
    link?: string;
    imageUrl: string;
    disabled?: boolean;
    titleColor?: string;
}

export const OrgOrAppMenuItem: React.FC<OrgOrAppMenuItemProps> = ({
    shouldShowText,
    onClick,
    shouldShowCircleIcon,
    title,
    link,
    imageUrl,
    appPlatform,
    disabled,
    titleColor,
}) => {
    const sc = useSidebarColors();
    const firstLetter = title.charAt(0).toUpperCase();

    const contents = (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                justifyContent: 'space-between',
                flex: 1,
                minWidth: 0,
            }}
        >
            <div
                style={{
                    display: 'flex',
                    gap: 18,
                    alignItems: 'center',
                    minWidth: 0,
                    opacity: disabled ? 0.5 : 1,
                    filter: disabled ? 'grayscale(100%)' : 'none',
                }}
            >
                <Avatar
                    shape={shouldShowCircleIcon ? 'circle' : 'square'}
                    size={27}
                    src={imageUrl}
                    style={{
                        flexShrink: 0,
                    }}
                >
                    {firstLetter}
                </Avatar>
                {shouldShowText && (
                    <div
                        style={{
                            display: 'flex',
                            gap: 8,
                            minWidth: 0,
                        }}
                    >
                        <Text
                            ellipsis
                            style={{
                                fontSize: 16,
                                color: titleColor ?? runwayColors.white,
                                minWidth: 0,
                            }}
                        >
                            {title}
                        </Text>

                        {appPlatform && (
                            <Text
                                ellipsis
                                style={{
                                    fontSize: 16,
                                    color: antGrey.grey7,
                                    wordBreak: 'normal',
                                    minWidth: 0,
                                }}
                            >
                                {displayTextForAppPlatform(appPlatform)}
                            </Text>
                        )}
                    </div>
                )}
            </div>
            {appPlatform && (
                <Icon component={iconComponentForPlatform(appPlatform)} style={{ color: sc.iconMuted, fontSize: 22 }} />
            )}
        </div>
    );

    if (link) {
        return (
            <Link
                onClick={onClick}
                to={link}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    pointerEvents: disabled ? 'none' : 'inherit',
                    justifyContent: 'space-between',
                    minWidth: 0,
                    ...(appPlatform ?
                        {
                            padding: '22px 20px',
                            backgroundColor: antGrey.grey8,
                        }
                    :   {}),
                }}
            >
                {contents}
            </Link>
        );
    }

    return contents;
};

export interface MobileOrgOrAppPickerProps {
    defaultOrgId: string;
}

export const MobileOrgOrAppPicker: React.FC<MobileOrgOrAppPickerProps> = ({ defaultOrgId }) => {
    const appRouteMatch = useRouteMatch<AppRouteComponentMatchParams>(routes.app);

    const selectedAppId = appRouteMatch ? appRouteMatch.params.appIdParam : null;

    const sc = useSidebarColors();
    const [isExpanded, setIsExpanded] = React.useState(false);
    const [searchText, setSearchText] = React.useState('');
    const user = useSelector((state: RootState) => selectUser(state));
    const avatarDisplayName = displayUserNameShort(user).charAt(0).toUpperCase();

    const allAppsInOrg = useSelector((state: RootState) => {
        const allAppIdsInOrg = selectAllAppsIdsInOrg(state, defaultOrgId);
        const allApps = allAppIdsInOrg.map((appId) => selectApp(state, appId));
        return compact(allApps);
    });

    const org = useSelector((state: RootState) => selectOrg(state, defaultOrgId));

    const orgRoute = createOrgRoute(defaultOrgId);

    const selectedApp = useSelector((state: RootState) => selectApp(state, selectedAppId));

    const filteredApps = React.useMemo(() => {
        if (!searchText) return allAppsInOrg;
        const removeDiacritics = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        const normalize = (s: string) => removeDiacritics(s.toLowerCase());
        const query = normalize(searchText);
        return allAppsInOrg.filter((app) => {
            const name = normalize(app.appSettings?.appName ?? '');
            const platform = normalize(displayTextForAppPlatform(app.appSettings?.platform) ?? '');
            return name.includes(query) || platform.includes(query);
        });
    }, [allAppsInOrg, searchText]);

    return (
        <div
            style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '22px 20px',
                position: 'relative',
                flexGrow: 1,
                alignItems: 'center',
                width: '100vw',
            }}
        >
            <div
                style={{
                    position: 'absolute',
                    opacity: isExpanded ? 1 : 0,
                    pointerEvents: isExpanded ? 'all' : 'none',
                    transition: 'all 0.2s',
                    top: '100%',
                    left: 0,
                    width: '100%',
                    zIndex: 10,
                    boxShadow: 'var(--rw-shadow-lg)',
                    backgroundColor: antGrey.grey8,
                }}
            >
                {isExpanded && (
                    <Space
                        size={0}
                        direction="vertical"
                        style={{
                            width: '100%',
                        }}
                        split={
                            <div
                                style={{
                                    height: 1,
                                    width: '100%',
                                    backgroundColor: antGrey.grey7,
                                }}
                            />
                        }
                    >
                        <Input
                            className="mobile-app-search-input"
                            prefix={<SearchOutlined style={{ color: antGrey.grey7, fontSize: 27, marginRight: 14 }} />}
                            placeholder="Search"
                            allowClear
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            autoFocus
                            style={{
                                padding: '22px 20px',
                                backgroundColor: antGrey.grey8,
                                border: 'none',
                                borderRadius: 0,
                                fontSize: 16,
                                color: runwayColors.white,
                            }}
                        />
                        {filteredApps.map((app) => {
                            const appRoute = createAppBuildsRoute(defaultOrgId, app.appId);

                            const title = app.appSettings.appName;
                            const imageUrl = app.appSettings.appImageUrl;
                            const appPlatform = app.appSettings.platform;

                            const isBuildDistroSupportedPlatform = getIsBuildDistroSupportedPlatform(appPlatform);

                            return (
                                <OrgOrAppMenuItem
                                    shouldShowText
                                    key={app.appId}
                                    link={appRoute}
                                    title={title}
                                    imageUrl={imageUrl}
                                    appPlatform={appPlatform}
                                    disabled={!isBuildDistroSupportedPlatform}
                                    onClick={() => {
                                        setIsExpanded(false);
                                        setSearchText('');
                                    }}
                                />
                            );
                        })}
                    </Space>
                )}
            </div>
            {isExpanded ?
                <>
                    <OrgOrAppMenuItem
                        shouldShowCircleIcon
                        shouldShowText
                        title={org.name}
                        imageUrl={org.orgImageUrl}
                        link={orgRoute}
                        titleColor={antGrey.grey1}
                        onClick={() => {
                            setIsExpanded(false);
                            setSearchText('');
                        }}
                    />
                    <CloseCircleFilled
                        style={{
                            color: sc.text,
                            fontSize: 28,
                        }}
                        onClick={() => {
                            setIsExpanded(false);
                            setSearchText('');
                        }}
                    />
                </>
            :   <>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            cursor: 'pointer',
                        }}
                        onClick={() => setIsExpanded(true)}
                    >
                        <OrgOrAppMenuItem
                            title={selectedApp?.appSettings?.appName ?? org.name}
                            imageUrl={selectedApp?.appSettings?.appImageUrl ?? org.orgImageUrl}
                            shouldShowCircleIcon={!selectedApp}
                            onClick={() => setIsExpanded(false)}
                        />
                        <CaretDownFilled
                            style={{
                                color: antGrey.grey7,
                                fontSize: 22,
                            }}
                        />
                    </div>
                    <Space size={20}>
                        <img src={LogoWordmark} />
                        <Link to={routes.user}>
                            <Avatar size={28} style={{ ...styles.userAvatar }} src={user.commsAvatars?.image48}>
                                {avatarDisplayName}
                            </Avatar>
                        </Link>
                    </Space>
                </>
            }
        </div>
    );
};
